import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Progress,
  Typography,
  InputNumber,
} from "antd";
import {
  EditOutlined,
  FileTextOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getPurchaseOrderById, deletePurchaseOrder, updatePurchaseOrderItemReceived } from "../../../redux/actions/purchase/purchaseOrderAction";
import PurchaseOrderPrint from "./PurchaseOrderPrint";

const { Title } = Typography;

const PurchaseOrderDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const order = useSelector((state) => state.purchaseOrders.currentOrder);
  const [editedItems, setEditedItems] = useState({});

  useEffect(() => {
    dispatch(getPurchaseOrderById(id));
  }, [dispatch, id]);

  const getDisplayValue = (record, field) =>
    editedItems[record.id]?.[field] !== undefined && editedItems[record.id]?.[field] !== null
      ? editedItems[record.id][field]
      : record[field];

  const setItemEdit = (itemId, field, value) => {
    setEditedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
  };

  const handleSaveReceived = async () => {
    const items = Object.entries(editedItems)
      .filter(([, e]) => Object.keys(e).length > 0)
      .map(([itemId, e]) => ({ item_id: Number(itemId), ...e }));
    if (items.length === 0) return;
    const result = await dispatch(updatePurchaseOrderItemReceived(id, { items }));
    if (result?.message === "success") setEditedItems({});
  };

  const hasEdits = Object.keys(editedItems).length > 0;

  if (!order) {
    return <div>Loading...</div>;
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      partial: "blue",
      received: "green",
      cancelled: "red",
    };
    return colors[status] || "default";
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this purchase order?")) {
      const result = await dispatch(deletePurchaseOrder(id));
      if (result?.message === "success") {
        navigate("/purchase-order");
      }
    }
  };

  const handleConvertToPurchase = () => {
    navigate(`/purchase?purchase_order_id=${id}`);
  };

  const handleConvertToSaleInvoice = () => {
    navigate(`/sale?purchase_order_id=${id}`);
  };

  const itemColumns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (product) => (
        <div>
          <div>{product?.name || "-"}</div>
          <div style={{ fontSize: "12px", color: "#999" }}>ISBN: {product?.isbn || "-"}</div>
        </div>
      ),
    },
    {
      title: "Ordered Qty",
      dataIndex: "ordered_quantity",
      key: "ordered_quantity",
    },
    {
      title: "Received Qty",
      dataIndex: "received_quantity",
      key: "received_quantity",
      render: (_, record) => (
        <InputNumber
          min={0}
          max={record.ordered_quantity}
          value={getDisplayValue(record, "received_quantity")}
          onChange={(val) => setItemEdit(record.id, "received_quantity", val ?? 0)}
          style={{ width: "80px" }}
        />
      ),
    },
    {
      title: "Pending Qty",
      key: "pending",
      render: (_, record) => record.ordered_quantity - (getDisplayValue(record, "received_quantity") ?? 0),
    },
    {
      title: "Price",
      dataIndex: "purchase_price",
      key: "purchase_price",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          prefix="₹"
          value={getDisplayValue(record, "purchase_price")}
          onChange={(val) => setItemEdit(record.id, "purchase_price", val ?? 0)}
          style={{ width: "120px" }}
        />
      ),
    },
    {
      title: "Received %",
      key: "received",
      render: (_, record) => {
        const received = getDisplayValue(record, "received_quantity") ?? 0;
        const percentage = record.ordered_quantity > 0 ? (received / record.ordered_quantity) * 100 : 0;
        return (
          <Progress
            percent={Math.round(percentage)}
            status={percentage === 100 ? "success" : percentage > 0 ? "active" : "normal"}
            size="small"
          />
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: "20px" }}>
          <Title level={3} style={{ margin: 0 }}>
            Purchase Order Details
          </Title>
          <Space wrap>
            <PurchaseOrderPrint order={order} />
            {order.status !== "received" && order.status !== "cancelled" && (
              <>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/purchase-order/edit/${id}`)}
                >
                  Edit
                </Button>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleConvertToPurchase}
                >
                  Convert to Purchase
                </Button>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleConvertToSaleInvoice}
                >
                  Convert to Invoice
                </Button>
              </>
            )}
            {order.status !== "received" && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>

        <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Order Number">{order.order_number}</Descriptions.Item>
          <Descriptions.Item label="Order Date">
            {moment(order.order_date).format("DD-MM-YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(order.status)}>{order.status.toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Supplier">{order.supplier?.name || "-"}</Descriptions.Item>
          <Descriptions.Item label="User">{order.user?.username || "-"}</Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            ₹{parseFloat(order.total_amount || 0).toFixed(2)}
          </Descriptions.Item>
          {order.expected_delivery_date && (
            <Descriptions.Item label="Expected Delivery">
              {moment(order.expected_delivery_date).format("DD-MM-YYYY")}
            </Descriptions.Item>
          )}
          {order.sales_order && (
            <Descriptions.Item label="Linked Sales Order">
              {order.sales_order.order_number}
            </Descriptions.Item>
          )}
          {order.note && (
            <Descriptions.Item label="Note" span={3}>
              {order.note}
            </Descriptions.Item>
          )}
        </Descriptions>

        <div style={{ marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Title level={4} style={{ margin: 0 }}>Order Items</Title>
            {hasEdits && (
              <Button type="primary" onClick={handleSaveReceived}>
                Save Received & Price
              </Button>
            )}
          </div>
          <Table
            columns={itemColumns}
            dataSource={order.order_items || []}
            rowKey="id"
            pagination={false}
          />
        </div>

        {order.purchaseInvoices && order.purchaseInvoices.length > 0 && (
          <div style={{ marginTop: "24px" }}>
            <Title level={4}>Linked Purchase Invoices</Title>
            <Table
              columns={[
                { title: "Invoice ID", dataIndex: "id", key: "id" },
                { title: "Date", dataIndex: "date", key: "date", render: (date) => moment(date).format("DD-MM-YYYY") },
                { title: "Amount", dataIndex: "total_amount", key: "total_amount", render: (amt) => `₹${parseFloat(amt || 0).toFixed(2)}` },
              ]}
              dataSource={order.purchaseInvoices}
              rowKey="id"
              pagination={false}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default PurchaseOrderDetails;
